import { useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import useBoardStore from '../../store/boardStore';
import { formatBRL, sumCurrency } from '../../utils/currency';
import { parseDateOnly } from '../../utils/dateOnly';

function useAggTooltip() {
  const [tip, setTip] = useState(null);
  const show = (e, label, count, total) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ label, count, total, x: rect.left + rect.width / 2, y: rect.top - 8 });
  };
  const hide = () => setTip(null);
  const node = tip && createPortal(
    <div
      style={{ position: 'fixed', top: tip.y, left: tip.x, transform: 'translate(-50%, -100%)', zIndex: 9999 }}
      className="bg-card border border-border rounded-md shadow-xl px-3 py-1.5 text-xs text-text-primary pointer-events-none whitespace-nowrap flex items-center gap-2"
    >
      <span className="font-medium">{tip.label}</span>
      <span className="text-text-secondary">{tip.count}/{tip.total}</span>
      <span className="font-semibold">{Math.round((tip.count / tip.total) * 100)}%</span>
    </div>,
    document.body
  );
  return { show, hide, node };
}

function parseDate(v) {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = parseDateOnly(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function normalizeDateValue(v) {
  if (!v) return { start: null, end: null };
  if (typeof v === 'string') return { start: parseDate(v), end: null };
  return { start: parseDate(v.start), end: parseDate(v.end) };
}

// M3c, item 3: resolve o valor bruto da célula pra status_option real —
// mesma regra de BoardTable.jsx (id do formato canônico {id,label}, com
// fallback pro label cru de valor antigo ainda não convertido).
function resolveStatusOption(opts, v) {
  if (v && typeof v === 'object' && v.id != null) {
    const byId = opts.find((o) => o.id === v.id);
    if (byId) return byId;
  }
  const label = v && typeof v === 'object' ? (v.label ?? null) : (typeof v === 'string' ? v : null);
  if (label == null) return null;
  return opts.find((o) => o.label === label) || null;
}

function StatusAgg({ column, cards, cardValues, statusOptions }) {
  const tip = useAggTooltip();
  const opts = statusOptions[column.id] || [];
  const counts = {};
  for (const c of cards) {
    const v = cardValues[`${c.id}:${column.id}`];
    const opt = resolveStatusOption(opts, v);
    if (!opt) continue;
    counts[opt.id] = (counts[opt.id] || 0) + 1;
  }
  const total = cards.length;
  if (!total) return <div className="h-3" />;
  // Inclui tanto "sem valor" quanto "valor aponta pra etiqueta que não existe
  // mais" — as duas ficam de fora de `counts`, e a barra sempre soma 100%.
  const countedTotal = Object.values(counts).reduce((a, b) => a + b, 0);
  const emptyCount = total - countedTotal;
  return (
    <>
      <div className="flex h-3 cursor-default">
        {opts.map((o) => {
          const n = counts[o.id] || 0;
          if (!n) return null;
          const pct = (n / total) * 100;
          return (
            <div
              key={o.id}
              style={{ backgroundColor: o.color, width: `${pct}%` }}
              className="relative transition-transform duration-150 origin-center hover:scale-y-[1.6] hover:z-10"
              onMouseEnter={(e) => tip.show(e, o.label, n, total)}
              onMouseLeave={tip.hide}
            />
          );
        })}
        {emptyCount > 0 && (
          <div
            style={{ backgroundColor: '#c4c4c4', flex: 1 }}
            className="relative transition-transform duration-150 origin-center hover:scale-y-[1.6] hover:z-10"
            onMouseEnter={(e) => tip.show(e, 'Sem status', emptyCount, total)}
            onMouseLeave={tip.hide}
          />
        )}
      </div>
      {tip.node}
    </>
  );
}

function SelectAgg({ column, cards, cardValues }) {
  const tip = useAggTooltip();
  const opts = Array.isArray(column.config?.options)
    ? column.config.options.map((o) => (typeof o === 'string' ? { label: o, color: '#c4c4c4' } : o))
    : [];
  const counts = {};
  for (const c of cards) {
    const v = cardValues[`${c.id}:${column.id}`];
    if (!v) continue;
    counts[v] = (counts[v] || 0) + 1;
  }
  const total = cards.length;
  if (!total) return <div className="h-3" />;
  return (
    <>
      <div className="flex h-3 cursor-default">
        {opts.map((o) => {
          const n = counts[o.label] || 0;
          if (!n) return null;
          return (
            <div
              key={o.label}
              style={{ backgroundColor: o.color, width: `${(n / total) * 100}%` }}
              className="relative transition-transform duration-150 origin-center hover:scale-y-[1.6] hover:z-10"
              onMouseEnter={(e) => tip.show(e, o.label, n, total)}
              onMouseLeave={tip.hide}
            />
          );
        })}
      </div>
      {tip.node}
    </>
  );
}

function DateAgg({ column, cards, cardValues }) {
  const dates = [];
  for (const c of cards) {
    const v = cardValues[`${c.id}:${column.id}`];
    const { start, end } = normalizeDateValue(v);
    if (start) dates.push(start);
    if (end) dates.push(end);
  }
  if (!dates.length) return <div className="text-[11px] text-text-secondary/50 text-center">—</div>;
  const min = new Date(Math.min(...dates.map((d) => d.getTime())));
  const max = new Date(Math.max(...dates.map((d) => d.getTime())));
  const f = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  const label = min.getTime() === max.getTime() ? f(min) : `${f(min)} – ${f(max)}`;
  return (
    <div className="flex justify-center">
      <span className="text-[11px] font-medium text-text-primary bg-accent/20 px-2 py-0.5 rounded-full whitespace-nowrap">
        {label}
      </span>
    </div>
  );
}

function NumberAgg({ column, cards, cardValues }) {
  let sum = 0;
  let count = 0;
  for (const c of cards) {
    const v = cardValues[`${c.id}:${column.id}`];
    if (v == null) continue;
    const n = Number(v);
    if (!Number.isNaN(n)) { sum += n; count++; }
  }
  if (!count) return <div className="text-[11px] text-text-secondary/50 text-center">—</div>;
  return (
    <div className="text-[11px] font-medium text-text-primary text-center">
      Σ {sum.toLocaleString('pt-BR')}
    </div>
  );
}

function CurrencyAgg({ column, cards, cardValues }) {
  // Só número entra na conta. A soma é algébrica (negativo subtrai) e feita em centavos
  // por sumCurrency, então um total negativo sai como "-R$ ..." e 0,1 + 0,2 dá 0,30.
  const amounts = [];
  for (const c of cards) {
    const v = cardValues[`${c.id}:${column.id}`];
    if (typeof v === 'number' && Number.isFinite(v)) amounts.push(v);
  }
  if (!amounts.length) return <div className="text-[11px] text-text-secondary/50 text-center">—</div>;
  const decimals = column.config?.decimals;
  return (
    <div className="text-[11px] font-medium text-text-primary text-center">
      <span className="whitespace-nowrap">{formatBRL(sumCurrency(amounts, decimals), decimals)}</span>
    </div>
  );
}

function CheckboxAgg({ column, cards, cardValues }) {
  const checked = cards.filter((c) => !!cardValues[`${c.id}:${column.id}`]).length;
  const total = cards.length;
  if (!total) return <div className="h-3" />;
  const pct = (checked / total) * 100;
  return (
    <div title={`${checked} / ${total}`}>
      <div className="h-3 rounded-sm overflow-hidden bg-white/5">
        <div className="h-full bg-success" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-text-secondary text-center mt-0.5">{checked}/{total}</div>
    </div>
  );
}

function PersonAgg({ column, cards, cardValues, users }) {
  const assigned = cards.map((c) => cardValues[`${c.id}:${column.id}`]).filter(Boolean);
  const unique = [...new Set(assigned)];
  if (!unique.length) return <div className="text-[11px] text-text-secondary/50 text-center">—</div>;
  return (
    <div className="flex items-center justify-center -space-x-1.5">
      {unique.slice(0, 4).map((id) => {
        const u = users.find((x) => x.id === id);
        const initials = u?.name ? u.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
        return (
          <div
            key={id}
            className="w-5 h-5 rounded-full bg-accent/30 text-accent flex items-center justify-center text-[9px] font-semibold border border-main"
            title={u?.name}
          >
            {u?.avatar_url ? <img src={u.avatar_url} alt={u.name} className="w-full h-full rounded-full object-cover" /> : initials}
          </div>
        );
      })}
      {unique.length > 4 && (
        <div className="w-5 h-5 rounded-full bg-white/10 text-text-secondary flex items-center justify-center text-[9px] font-semibold border border-main">
          +{unique.length - 4}
        </div>
      )}
    </div>
  );
}

function GroupFooter({ group, columns, cards }) {
  // Selectors granulares com useShallow: cardValues/statusOptions/users nao mudam
  // durante o drag, entao o GroupFooter nao re-renderiza a cada move otimista.
  // (Antes: useBoardStore() sem selector assinava a store inteira e re-renderizava
  // todos os footers a cada set() do moveCardLocal — custo alto e gratuito.)
  const { cardValues, statusOptions, users } = useBoardStore(
    useShallow((s) => ({
      cardValues: s.cardValues,
      statusOptions: s.statusOptions,
      users: s.users,
    }))
  );

  const renderAgg = (col) => {
    switch (col.type) {
      case 'status':
        return <StatusAgg column={col} cards={cards} cardValues={cardValues} statusOptions={statusOptions} />;
      case 'select':
        return <SelectAgg column={col} cards={cards} cardValues={cardValues} />;
      case 'date':
        return <DateAgg column={col} cards={cards} cardValues={cardValues} />;
      case 'number':
        return <NumberAgg column={col} cards={cards} cardValues={cardValues} />;
      case 'currency':
        return <CurrencyAgg column={col} cards={cards} cardValues={cardValues} />;
      case 'checkbox':
        return <CheckboxAgg column={col} cards={cards} cardValues={cardValues} />;
      case 'person':
        return <PersonAgg column={col} cards={cards} cardValues={cardValues} users={users} />;
      default:
        return <div className="h-3" />;
    }
  };

  return (
    <tr>
      <td className="text-left p-0 border-r border-[color:var(--group-border)] sticky left-0 z-10" style={{ backgroundColor: 'var(--group-bg)' }}>
        <div className="flex items-center h-full">
          <div className="w-1.5 self-stretch shrink-0 rounded-bl-lg min-h-[36px]" style={{ backgroundColor: group.color }} />
          <div className="flex-1 px-3 py-2 text-[11px] text-text-secondary">
            {cards.length} {cards.length === 1 ? 'item' : 'itens'}
          </div>
        </div>
      </td>
      {columns.map((col) => (
        <td key={col.id} className="border-r border-[color:var(--group-border)] px-2 py-2" style={{ width: col.width || 150 }}>
          {renderAgg(col)}
        </td>
      ))}
      <td className="w-10" />
    </tr>
  );
}

// memo com comparacao shallow: props (group/columns/cards) chegam estaveis do
// GroupSection memoizado, entao footers de grupos nao tocados nao re-renderizam.
export default memo(GroupFooter);
